import { useEffect, useState } from 'react';
import { Plus, Refresh, Search } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { Input } from 'components/ui/input';
import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import ConfigurationHeader from 'components/configuration/ConfigurationHeader';
import ConfigurationHeading from 'components/configuration/ConfigurationHeading';
import ConfigurationButton from 'components/configuration/ConfigurationButton';
import { ProjectFolderIcon } from 'components/blocks/layout/project-folder-icon';
import { configureCardGridClassName } from 'constant/configure-layout';
import { useProjectsPage } from 'features/projects/hooks/use-projects';
import { NewProjectModal } from 'features/projects/components/new-project-modal';
import { setProjectDialog } from 'features/projects/components/project-actions';
import type { Project } from 'features/projects/types/create-project';
import { ToolsPagination } from 'pages/tools/tools-pagination';
import { ProjectCard } from './components/project-card';

const PROJECTS_PAGE_SIZE = 30;

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);

  const trimmedSearch = search.trim();
  const { data, isLoading } = useProjectsPage({
    page,
    pageSize: PROJECTS_PAGE_SIZE,
    order: 'desc',
    searchQuery: trimmedSearch,
  });

  const projects = data?.projects ?? [];
  const totalCount = data?.totalCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PROJECTS_PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [trimmedSearch]);

  // Deleting the last project on the last page would otherwise leave an empty page.
  useEffect(() => {
    if (!isLoading && totalCount > 0 && page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount, isLoading, totalCount]);

  const openProject = (project: Project) => navigate(`/project/${encodeURIComponent(project.id)}`);

  const openDialog = (type: 'rename' | 'delete', project: Project) =>
    setProjectDialog({ type, projectId: project.id, projectName: project.name, projectColor: project.color });

  const renderList = () => {
    if (isLoading) {
      return (
        <Card className="rounded-lg border border-grey-50 bg-white shadow-none dark:border-border dark:bg-card">
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Refresh width={16} height={16} strokeWidth={1.75} className="animate-spin" />
            {t('LOADING_PROJECTS', { defaultValue: 'Loading projects...' })}
          </div>
        </Card>
      );
    }

    if (projects.length === 0) {
      return (
        <Card className="flex min-h-80 w-full flex-col items-center justify-center gap-6 rounded-lg border border-grey-100 bg-white p-6 shadow-none dark:border-border dark:bg-card">
          <div className="flex flex-col items-center gap-1.5 text-center">
            <p className="text-sm font-semibold leading-heading text-grey-900 dark:text-foreground">
              {t('NO_PROJECTS_FOUND', { defaultValue: 'No projects found' })}
            </p>
            <p className="text-xs font-normal leading-heading text-grey-500 dark:text-muted-foreground">
              {trimmedSearch
                ? t('NO_PROJECTS_MATCHED_SEARCH', { defaultValue: 'No projects matched your search.' })
                : t('GET_STARTED_CREATE_FIRST_PROJECT', {
                    defaultValue: 'Get started by creating your first project.',
                  })}
            </p>
          </div>
          {!trimmedSearch ? (
            <Button
              type="button"
              variant="outline"
              className="h-10 shrink-0 rounded-full border border-indigo-100 bg-gradient-to-br from-white to-indigo-25 px-6 text-base font-medium leading-heading text-indigo-950 shadow-none hover:opacity-90 dark:border-border dark:from-card dark:to-card dark:text-foreground dark:hover:opacity-100"
              onClick={() => setIsNewProjectOpen(true)}
            >
              {t('CREATE_PROJECT') || 'Create Project'}
            </Button>
          ) : null}
        </Card>
      );
    }

    return (
      <>
        <div className={configureCardGridClassName}>
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onSelect={openProject}
              onRenameClick={(item) => openDialog('rename', item)}
              onDeleteClick={(item) => openDialog('delete', item)}
            />
          ))}
        </div>
        <ToolsPagination page={page} pageCount={pageCount} onPageChange={setPage} />
      </>
    );
  };

  return (
    <ConfigurationLayout>
      <div className="flex w-full min-w-0 flex-col gap-6 border-b border-grey-100 pb-6 dark:border-border">
        <div className="flex w-full min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <ConfigurationHeader
            title={t('PROJECTS_TITLE', { defaultValue: 'Projects' })}
            subtitle={t('WHAT_ARE_PROJECTS_DESCRIPTION', {
              defaultValue: 'Projects let you organize chats and add instructions & files to groups of chats.',
            })}
            icon={<ProjectFolderIcon />}
          />
          <ConfigurationButton icon={<Plus className="h-4 w-4" />} onClick={() => setIsNewProjectOpen(true)}>
            {t('CREATE_PROJECT') || 'Create Project'}
          </ConfigurationButton>
        </div>

        <div className="flex h-12 w-full min-w-0 items-center gap-3 rounded-lg border border-grey-100 bg-white p-3 sm:max-w-md dark:border-border dark:bg-card">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center">
            <Search width={15} height={15} strokeWidth={1.75} className="text-grey-400" />
          </div>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('SEARCH_PROJECTS_PLACEHOLDER', { defaultValue: 'Search projects...' })}
            className="h-full min-h-0 flex-1 border-0 bg-transparent p-0 text-sm font-normal leading-heading shadow-none placeholder:text-grey-400 focus-visible:ring-0 dark:bg-transparent"
          />
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-3 pb-8">
        <ConfigurationHeading title={t('', { defaultValue: 'All projects' })} />
        {renderList()}
      </div>

      <NewProjectModal isOpen={isNewProjectOpen} onClose={() => setIsNewProjectOpen(false)} />
    </ConfigurationLayout>
  );
}
